import { FlexRow } from '@/src/components'
import { FormNumericInput } from '@/src/components/Form/FormNumericInput'
import { InfoBox } from '@/src/components/InfoBox'
import { ALL_LOCATIONS, useLocationsContext } from '@/src/contexts/LocationsContext'
import type { LoadPlanningAdjustmentFormEmptyable } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import { useAggregatedMonthlyBaseloads } from '@/src/hooks/useAggregatedMonthlyBaseloads.hook'
import { appColors } from '@/src/theme'
import { formatMeasurement } from '@/src/utils/formatMeasurement'
import { Box } from '@chakra-ui/react'
import type { FormikProps } from 'formik'
import { useTranslation } from 'react-i18next'

interface CompanyHolidayAdjustmentFormProps {
  formik: FormikProps<LoadPlanningAdjustmentFormEmptyable>
}

export const CompanyHolidayAdjustmentForm = ({ formik }: CompanyHolidayAdjustmentFormProps) => {
  const { t } = useTranslation()

  const currentYear = new Date().getFullYear()
  const targetYear = currentYear - 1

  const { companyPgId } = useLocationsContext()
  const isCompanySelected = formik.values.locationId === ALL_LOCATIONS
  const { data } = useAggregatedMonthlyBaseloads(
    {
      companyId: companyPgId,
      locationId: isCompanySelected ? undefined : formik.values.locationId,
      years: [targetYear],
    },
    { enabled: !!formik.values.locationId && !!companyPgId }
  )

  const average = data
    ? formatMeasurement(data?.reduce((total, d) => (d.yearValues?.[targetYear]?.value ?? 0) + total, 0) / data.length, 'kW')
    : '-'

  return (
    <>
      <FormNumericInput
        isRequired={true}
        onChange={(value, _1, meta) => {
          if (meta.float) {
            return formik.setFieldValue('baseLoad', Math.abs(meta.float))
          }
          formik.setFieldValue('baseLoad', value)
        }}
        name='baseLoad'
        label={'Grundlast'}
        value={formik.values.baseLoad}
        fontWeight={'86'}
        labelFontSize={'14px'}
        labelFontColor={appColors.secondaryForeground}
        paddingLeft={'10px'}
        width={'full'}
        labelRight='kW'
        componentStyle={{ paddingRight: '36px' }}
        textAlign={'right'}
        min={0}
      />
      {formik.values.locationId && (
        <InfoBox
          tooltipLabel={t('components.procurement.forecasting.modal.form.baseload_year_tooltip')}
          description={
            <FlexRow justifyContent={'space-between'} width={'100%'}>
              <Box>{t('components.procurement.forecasting.modal.form.baseload_year', { year: targetYear })}</Box>
              <Box>{average}</Box>
            </FlexRow>
          }
        />
      )}
    </>
  )
}
